import { LayoutService } from '@/app/layout/service/layout.service';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AppConfigurator } from './app.configurator';
import { AppSidebar } from './app.sidebar';
import { AppHeader } from './app.header';
import { SessionService } from '@/app/core/services';
import { UserStateService } from '@/app/features/user/services';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppHeader,
    AppSidebar,
    RouterModule,
    AppConfigurator,
    ButtonModule,
    DialogModule,
    ProgressBarModule,
    ProgressSpinnerModule,
  ],
  template: `<div
      class="layout-wrapper h-dvh bg-primary-50 dark:bg-surface-950 relative p-2 flex overflow-hidden"
      [ngClass]="containerClass()"
    >
      <div app-sidebar></div>
      <main
        class="layout-content-wrapper flex-1 max-w-[1720px] w-full mx-auto flex flex-col transition-all duration-300 h-full bg-background rounded-3xl shadow-stroke overflow-hidden"
      >
        <header app-header class="layout-topbar !z-40"></header>

        <div class="p-6 flex-1 overflow-auto scrollable-content">
          <router-outlet></router-outlet>
        </div>

        <div class="px-6 pb-2 text-right">
          <a
            routerLink="/landing/privacy-policy"
            class="text-xs text-surface-400 hover:text-surface-950 dark:hover:text-surface-0"
          >
            Política de Privacidad
          </a>
        </div>
      </main>
      <app-configurator />
      <div class="layout-mask"></div>
    </div>

    <p-dialog
      [visible]="showWarning()"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      header="Sesión a punto de expirar"
      [style]="{ width: '380px' }"
    >
      <div class="flex flex-col items-center justify-center gap-3 py-2">
        <i
          class="pi pi-clock"
          style="font-size: 2.5rem; color: var(--p-primary-color)"
        ></i>
        <p class="text-center m-0">
          Por inactividad, tu sesión se cerrará automáticamente en
        </p>
        <span
          style="font-size: 2rem; font-weight: 700; color: var(--p-primary-color)"
        >
          {{ secondsRemaining() }}s
        </span>
        <p class="text-center m-0 text-sm text-color-secondary">
          Haz clic en <strong>Continuar</strong> para mantener la sesión activa.
        </p>
      </div>
      <ng-template pTemplate="footer">
        <button pButton class="w-full" (click)="sessionService.extendSession()">
          Continuar sesión
        </button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [visible]="logoutLoading()"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      [showHeader]="false"
      [style]="{ width: '280px' }"
    >
      <div class="flex flex-col items-center justify-center gap-2 py-2">
        <p-progressSpinner
          styleClass="w-8 h-8"
          strokeWidth="4"
        ></p-progressSpinner>
        <p class="text-center m-0 font-medium">Cerrando sesión...</p>
      </div>
    </p-dialog>`,
})
export class AppLayout {
  layoutService = inject(LayoutService);
  sessionService = inject(SessionService);
  private readonly userStateService = inject(UserStateService);

  showWarning = this.sessionService.showWarning;
  secondsRemaining = this.sessionService.secondsRemaining;
  logoutLoading = this.userStateService.logoutLoading;

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-compact': layoutConfig.menuMode === 'compact',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-overlay-active': layoutState.overlayMenuActive,
      'layout-mobile-active': layoutState.mobileMenuActive,
      'layout-static-inactive': layoutState.staticMenuInactive,
      'layout-sidebar-expanded': layoutState.sidebarExpanded,
      'layout-sidebar-anchored': layoutState.anchored,
      [`layout-sidebar-${layoutConfig.darkTheme ? 'dark' : 'light'}`]: true,
    };
  });

  constructor() {
    effect(() => {
      const userData = this.userStateService.userData();
      if (userData?.id) {
        this.sessionService.startTracking();
      }
    });

    effect(() => {
      if (!this.userStateService.isLoggedIn()) {
        this.sessionService.stopTracking();
      }
    });
  }
}
